import { useFormik } from 'formik'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

import { companyAPI } from '../api/services/company'
import { CompanyWithRepresentative } from '../api/services/company/types.ts'
import { useAuth } from '../context/auth'
import { isUserExistsInGroup } from '../utils/userRights.ts'

export const useBusinessDetailsForm = () => {
  const { state } = useAuth()
  const isRestrictedUser = isUserExistsInGroup(
    state.groups,
    'dd_panel_restricted_users'
  )
  const [loading, setLoading] = useState(false)
  const [company, setCompany] = useState<CompanyWithRepresentative | null>(null)
  const [initialValues, setInitialValues] = useState<CompanyWithRepresentative>(
    {
      representative_first_name: '',
      representative_last_name: '',
      company_name: '',
      address1: '',
      address2: '',
      building: '',
      flat_number: '',
      postal_code: '',
      city: '',
      nip: ''
    }
  )

  useEffect(() => {
    const getCompany = async () => {
      setLoading(true)
      try {
        const response = await companyAPI.getCompany()
        const existsErrors =
          response?.error && Object.keys(response.error).length > 0

        if (existsErrors) {
          if (response.status === 404) {
            toast.error('Nie znaleziono')
          }
        }

        if (response.status === 200 && response.data.length > 0) {
          setInitialValues(response.data[0])
          setCompany(response.data[0])
        }
      } catch (error) {
        toast.error(error as string)
      } finally {
        setLoading(false)
      }
    }
    ;(async () => await getCompany())()
  }, [])

  const onSubmitSaveData = async (
    values: {
      representative_first_name: string
      representative_last_name: string
      company_name: string
      address1: string
      address2: string
      building: string
      flat_number: string
      postal_code: string
      city: string
      nip: string
    },
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    const toastId = toast.loading('Wysyłanie danych')
    setSubmitting(true)

    try {
      const response = await companyAPI.createCompany(values)

      const existsErrors =
        response?.error && Object.keys(response.error).length > 0

      if (existsErrors) {
        if (response.status === 404) {
          toast.error(response.error)
        }
      }

      if (response.status === 200) {
        formik.setStatus(true)
        isRestrictedUser
          ? localStorage.setItem(
              'agreement-' + state.email,
              JSON.stringify({ user: state.email, step: '2' })
            )
          : null
        setCompany(values)
        toast.update(toastId, {
          render: 'Dane firmowe zostały zapisane',
          type: 'success',
          delay: 500,
          isLoading: false,
          autoClose: 4000
        })
      }
      if (response.status === 400) {
        toast.dismiss(toastId)
      }
    } catch {
      toast.dismiss(toastId)
    } finally {
      setSubmitting(false)
    }
  }

  const isValidNIP = (value: string) => {
    if (!value) return false
    // Usuwa myślniki i spacje
    const nip = value.replace(/[\s-]/g, '')
    if (!/^\d{10}$/.test(nip)) return false
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
    const sum = weights.reduce(
      (acc, weight, idx) => acc + weight * +nip[idx],
      0
    )
    const control = sum % 11
    return control === +nip[9]
  }

  const validationSchema = Yup.object({
    representative_first_name: Yup.string().required('To pole jest wymagane'),
    representative_last_name: Yup.string().required('To pole jest wymagane'),
    company_name: Yup.string().required('To pole jest wymagane'),
    address1: Yup.string().required('To pole jest wymagane'),
    building: Yup.string().required('To pole jest wymagane'),
    postal_code: Yup.string()
      .required('To pole jest wymagane')
      .matches(
        /^\d{2}-\d{3}$/,
        'Nieprawidłowy format kodu pocztowego. Wymagany format: XX-XXX'
      ),

    city: Yup.string().required('To pole jest wymagane'),
    nip: Yup.string()
      .required('To pole jest wymagane')
      .test('nip', 'Nieprawidłowy numer NIP', isValidNIP)
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      await onSubmitSaveData(values, setSubmitting)
    },
    enableReinitialize: true
  })

  return { formik, loading, company }
}
